/**
 * @file: TaskStatusInfo.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { VehicleInspectionTask } from '@euler/model/entity';
import {
  CommonTaskStatus,
  InspectionTaskStatus,
  ReceptionOrderStatus,
} from '@euler/model/enum';
import { TaskDetail } from '@euler/model/task-detail';
import { VehicleServiceRecord } from '@euler/model/VehicleServiceRecord';
import { Maybe } from '@euler/types';
import { dateValueEquals } from '@euler/utils';

export type TaskStatusInfo = {
  status: InspectionTaskStatus;
  preInspectionStatus: Maybe<CommonTaskStatus>;
  inspectionStatus: Maybe<CommonTaskStatus>;
  constructionStatus: Maybe<CommonTaskStatus>;
  deliveryCheckStatus: Maybe<CommonTaskStatus>;
  isObdInspected: Maybe<boolean>;
  isConstructionJobScheduled: Maybe<boolean>;
  finishedAt: Maybe<string | Date>;
};

export function getTaskStatusInfoFromTask(
  task: VehicleInspectionTask,
): TaskStatusInfo {
  return {
    status: task.status,
    preInspectionStatus: task.preInspectionStatus,
    inspectionStatus: task.inspectionStatus,
    constructionStatus: task.constructionStatus,
    isConstructionJobScheduled: task.isConstructionJobScheduled,
    deliveryCheckStatus: task.deliveryCheckStatus,
    isObdInspected: task.isObdInspected,
    finishedAt: task.finishedAt ?? undefined,
  };
}

export function getTaskStatusInfoFromServiceRecord(
  record: VehicleServiceRecord,
): TaskStatusInfo {
  return {
    status: InspectionTaskStatus.Finished,
    preInspectionStatus: record.preInspectionStatus!,
    inspectionStatus: record.inspectionStatus!,
    constructionStatus: record.constructionStatus!,
    isConstructionJobScheduled: record.isConstructionJobScheduled!,
    deliveryCheckStatus: record.deliveryCheckStatus!,
    isObdInspected: record.isObdInspected!,
    finishedAt: record.finishedAt!,
  };
}

export function getTaskStatusFromTaskDetail(
  detail: TaskDetail,
): TaskStatusInfo {
  return {
    status: detail.basicInfo.status,
    preInspectionStatus: detail.preInspection.status,
    inspectionStatus: detail.inspection.status,
    constructionStatus: detail.construction.status,
    isConstructionJobScheduled: detail.construction.isScheduled,
    deliveryCheckStatus: detail.deliveryCheck.status,
    isObdInspected: detail.obdInspection.isInspected,
    finishedAt: detail.basicInfo.finishedAt,
  };
}

export function isTaskStatusChanged(a: TaskStatusInfo, b: TaskStatusInfo) {
  return (
    a.status !== b.status ||
    a.preInspectionStatus !== b.preInspectionStatus ||
    a.inspectionStatus !== b.inspectionStatus ||
    a.constructionStatus !== b.constructionStatus ||
    a.deliveryCheckStatus !== b.deliveryCheckStatus ||
    a.isObdInspected !== b.isObdInspected ||
    !dateValueEquals(a.finishedAt, b.finishedAt)
  );
}

export function taskStatusFromOrderStatus(orderStatus: ReceptionOrderStatus) {
  switch (orderStatus) {
    case ReceptionOrderStatus.Pending:
      return InspectionTaskStatus.Pending;
    case ReceptionOrderStatus.InProgress:
      return InspectionTaskStatus.InProgress;
    case ReceptionOrderStatus.Finished:
      return InspectionTaskStatus.Finished;
    case ReceptionOrderStatus.Cancelled:
      return InspectionTaskStatus.Cancelled;
  }
}
